import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../PublicLayout/PublicLayout';
import '../PrivacyPage/PrivacyPage.css';

const LAST_UPDATED = 'May 13, 2026';

function TermsPage() {
  return (
    <PublicLayout>
      <div className="legal-page">
        <div className="legal-page__container">

          <div className="legal-page__hero">
            <h1 className="legal-page__title">Terms of Service</h1>
            <p className="legal-page__meta">Last updated: {LAST_UPDATED}</p>
            <p className="legal-page__intro">
              These Terms of Service govern your use of Remori. Please read them
              carefully. By creating an account or using the service, you agree
              to be bound by these terms.
            </p>
          </div>

          <div className="legal-page__body">

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">1. Acceptance of terms</h2>
              <p>
                By accessing or using Remori (the "Service") — including the web
                app at remori-journal.com and the iOS application — you agree to
                these Terms of Service ("Terms"). If you do not agree, do not use
                the Service.
              </p>
              <p>
                We may update these Terms at any time. When we do, we will notify
                you by updating the "Last updated" date. Continued use of the
                Service after changes are posted constitutes acceptance of the
                revised Terms.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">2. Eligibility</h2>
              <p>
                You must be at least 13 years old to use Remori. By creating an
                account, you represent that you meet this age requirement and that
                all registration information you provide is accurate and complete.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">3. Your account</h2>
              <p>
                You are responsible for maintaining the confidentiality of your
                account credentials and for all activity that occurs under your
                account. You agree to notify us immediately at{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>{' '}
                if you suspect unauthorized access.
              </p>
              <p>
                You may not create more than one free account per person. You may
                not share your account with or transfer it to any other person.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">4. Your content</h2>
              <p>
                You retain full ownership of all journal entries and other content
                you submit to Remori ("Your Content"). By using the Service, you
                grant us a limited, non-exclusive, non-transferable license to
                store, process, and display Your Content solely to provide and
                improve the Service to you.
              </p>
              <p>
                We do not claim ownership of Your Content. We will not use it for
                advertising, sell it, or share it with third parties except as
                described in our{' '}
                <Link to="/privacy" className="legal-page__link">Privacy Policy</Link>.
              </p>
              <p>
                You are solely responsible for Your Content. By submitting
                content, you represent that you have the right to do so and that
                it does not violate any applicable law or third-party rights.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">5. Acceptable use</h2>
              <p>You agree not to use Remori to:</p>
              <ul className="legal-page__list">
                <li>
                  Violate any applicable law, regulation, or third-party rights.
                </li>
                <li>
                  Transmit any content that is unlawful, defamatory, harassing,
                  or otherwise objectionable.
                </li>
                <li>
                  Attempt to gain unauthorized access to any part of the Service
                  or its infrastructure.
                </li>
                <li>
                  Reverse-engineer, decompile, or disassemble any part of the
                  Service.
                </li>
                <li>
                  Use automated means (bots, scrapers, scripts) to access or
                  interact with the Service without our prior written consent.
                </li>
                <li>
                  Impersonate any person or entity, or misrepresent your
                  affiliation with any person or entity.
                </li>
              </ul>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">6. Service availability</h2>
              <p>
                We aim to provide a reliable service, but we do not guarantee that
                Remori will be available at all times without interruption or
                error. We may modify, suspend, or discontinue any part of the
                Service at any time with reasonable notice where practicable.
              </p>
              <p>
                We are not liable for any loss of data or interruption to your
                access caused by events outside our reasonable control. We
                strongly recommend using the Export Entries feature (Settings →
                Export Entries) to maintain your own backup.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">7. Intellectual property</h2>
              <p>
                All elements of the Remori Service — including the design,
                software, trademarks, and branding — are owned by or licensed to
                devhopeslabs and are protected by applicable intellectual property
                laws. Nothing in these Terms grants you any right to use our
                trademarks or branding.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">8. Termination</h2>
              <p>
                You may stop using the Service and delete your account at any time
                by emailing{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>
                .
              </p>
              <p>
                We may suspend or terminate your access to the Service immediately
                and without prior notice if we believe you have violated these
                Terms. Upon termination, your right to use the Service ceases. We
                will handle your data in accordance with our{' '}
                <Link to="/privacy" className="legal-page__link">Privacy Policy</Link>.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">9. Disclaimer of warranties</h2>
              <p>
                The Service is provided on an "as is" and "as available" basis,
                without warranties of any kind, either express or implied,
                including but not limited to implied warranties of merchantability,
                fitness for a particular purpose, and non-infringement.
              </p>
              <p>
                We do not warrant that the Service will be error-free, secure, or
                uninterrupted, or that any defects will be corrected.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">10. Limitation of liability</h2>
              <p>
                To the fullest extent permitted by applicable law, devhopeslabs
                and its operators shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages, or any loss of profits
                or revenue, whether incurred directly or indirectly, or any loss of
                data, use, or goodwill, arising from your use of or inability to
                use the Service.
              </p>
              <p>
                In no event shall our total aggregate liability exceed the amount
                you paid to us in the twelve months preceding the claim, or USD
                $10 if no payments were made.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">11. Governing law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with
                applicable law. Any disputes arising from these Terms or the
                Service shall be resolved in good faith first through direct
                communication with us at{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">12. Contact</h2>
              <p>
                If you have questions about these Terms, please contact us at:{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default TermsPage;
